import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import { CurecallButton, ECurecallButtonAppearance, ECurecallSize, TrashIcon } from '@honestica/curecall-ui'
import { MedAlertSection } from '../med-alert-section.component'
import { ActionDashboardSection } from '../dashboard-section.component'
import { TNodeFormValues, TNodeIntegerRangeVariable } from '../../../node.type'
import { useChatbotFlow } from '../../../chatbot.provider'
import { twMerge } from 'src/utils/tw-merge.util'

const DEBOUNCE_DELAY = 300

type AutoUpdatedBounds = Array<{ rangeIndex: number; boundType: 'lower' | 'upper' }>

interface IntegerRangeCardProps {
  rangeIndex: number
  isFirstRange: boolean
  isLastRange: boolean
  minValue: number
  maxValue: number
  autoUpdatedBounds: AutoUpdatedBounds
  onDelete: () => void
  onLowerBoundChange: (rangeIndex: number, value: number) => void
  onUpperBoundChange: (rangeIndex: number, value: number) => void
}

export function IntegerRangeCard({
  rangeIndex,
  isFirstRange,
  isLastRange,
  minValue,
  maxValue,
  autoUpdatedBounds,
  onDelete,
  onLowerBoundChange,
  onUpperBoundChange,
}: IntegerRangeCardProps) {
  const { t } = useTranslation()
  const { watch, register } = useFormContext<TNodeFormValues>()
  const { updateIntegerRangeAction } = useChatbotFlow()
  const upperBoundInputRef = useRef<HTMLInputElement | null>(null)
  const variable = watch('variable') as TNodeIntegerRangeVariable
  const integerRangeCondition = variable.condition
  const range = integerRangeCondition?.ranges[rangeIndex]

  const lowerBoundValue = range && !isNaN(range.lowerBound) ? range.lowerBound : minValue
  const upperBoundValue = range?.upperBound ?? NaN

  const prevRange = rangeIndex > 0 ? integerRangeCondition?.ranges[rangeIndex - 1] : null
  const minLowerBound = prevRange && !isNaN(prevRange.upperBound) ? prevRange.upperBound + 1 : minValue

  const debouncedLowerBoundChange = useDebouncedCallback((value: number) => {
    onLowerBoundChange(rangeIndex, value)
  }, DEBOUNCE_DELAY)

  const debouncedUpperBoundChange = useDebouncedCallback((value: number) => {
    onUpperBoundChange(rangeIndex, value)
  }, DEBOUNCE_DELAY)

  const upperBoundRegistration = register(`variable.condition.ranges.${rangeIndex}.upperBound`, {
    valueAsNumber: true,
    min: lowerBoundValue,
    max: maxValue,
    onChange: (e) => {
      const value = parseInt(e.target.value, 10)
      if (!isNaN(value)) {
        debouncedUpperBoundChange(value)
      }
    },
  })

  useEffect(() => {
    if (isLastRange && isNaN(upperBoundValue) && upperBoundInputRef.current) {
      upperBoundInputRef.current.focus()
    }
  }, [isLastRange, upperBoundValue])

  if (!range) return null

  const canDelete = !isFirstRange || !isLastRange

  const isLowerBoundFixed = isFirstRange

  const isUpperBoundFixed =
    isLastRange &&
    !isNaN(upperBoundValue) &&
    upperBoundValue === maxValue &&
    integerRangeCondition?.ranges.length > 1

  const isLowerBoundAutoUpdated = autoUpdatedBounds.some(
    (bound) => bound.rangeIndex === rangeIndex && bound.boundType === 'lower',
  )
  const isUpperBoundAutoUpdated = autoUpdatedBounds.some(
    (bound) => bound.rangeIndex === rangeIndex && bound.boundType === 'upper',
  )

  return (
    <div className="c-border c-border-gray-400 c-rounded-lg c-p-4 c-bg-white">
      <div className="c-flex c-items-center c-justify-between c-mb-4 c-gap-3">
        <div className="c-flex c-items-center c-gap-2 c-flex-1">
          <span className="c-text-sm c-font-semibold c-text-gray-900">
            {t('pages.configuration.followups.chatbot.conditional-sections.range-title')}
          </span>
          {isLowerBoundFixed ? (
            <span className="c-px-3 c-py-2 c-text-sm c-font-medium c-text-gray-900">{lowerBoundValue}</span>
          ) : (
            <input
              type="number"
              {...register(`variable.condition.ranges.${rangeIndex}.lowerBound`, {
                valueAsNumber: true,
                min: minLowerBound,
                max: !isNaN(upperBoundValue) ? upperBoundValue : maxValue,
                onChange: (e) => {
                  const value = parseInt(e.target.value, 10)
                  if (!isNaN(value)) {
                    debouncedLowerBoundChange(value)
                  }
                },
              })}
              className={twMerge(
                'c-w-20 c-px-3 c-py-2 c-border c-border-gray-400 c-rounded-md c-text-sm',
                isLowerBoundAutoUpdated && 'c-animate-input-flash',
              )}
              min={minLowerBound}
              max={!isNaN(upperBoundValue) ? upperBoundValue : maxValue}
            />
          )}
          <span className="c-text-sm c-font-semibold c-text-gray-900">
            {t('pages.configuration.followups.chatbot.conditional-sections.to')}
          </span>
          {isUpperBoundFixed ? (
            <span className="c-px-3 c-py-2 c-text-sm c-font-medium c-text-gray-900">{upperBoundValue}</span>
          ) : (
            <input
              type="number"
              {...upperBoundRegistration}
              ref={(e) => {
                upperBoundRegistration.ref(e)
                upperBoundInputRef.current = e
              }}
              className={twMerge(
                'c-w-20 c-px-3 c-py-2 c-border c-border-gray-400 c-rounded-md c-text-sm',
                isUpperBoundAutoUpdated && 'c-animate-input-flash',
              )}
              placeholder="?"
              min={lowerBoundValue}
              max={maxValue}
            />
          )}
        </div>
        {canDelete && (
          <CurecallButton
            iconType={<TrashIcon />}
            isGhostButton
            onClick={onDelete}
            appearance={ECurecallButtonAppearance.CANCEL}
            size={ECurecallSize.SMALL}
            ariaLabelText={t('common.actions.delete')}
          />
        )}
      </div>

      <div className="c-flex c-gap-2 c-flex-wrap">
        <MedAlertSection
          actionPath={`variable.condition.ranges.${rangeIndex}.action`}
          onUpdate={(action) => updateIntegerRangeAction(rangeIndex, action)}
        />

        <ActionDashboardSection
          actionPath={`variable.condition.ranges.${rangeIndex}.action`}
          onUpdate={(action) => updateIntegerRangeAction(rangeIndex, action)}
        />
      </div>
    </div>
  )
}
